import {
    assertIsNotArray,
    assertIsNumber,
    assertIsObject,
    assertIsTrue,
    assertIsUUID4,
    assertObjectHasKey,
} from '../../lib/test-and-assert/assert-base';
import {
    testIsArray,
    testIsFunction,
    testIsNonEmptyString,
    testIsNumber,
    testIsObject,
    testIsUndefined,
} from '../../lib/test-and-assert/test-base';

import { globalLogger } from '../../qaamgo/helper/global-logger';

import { JobInfoHelper } from '../uploader/core/info-helper/job-info-helper';
import { InputInfoHelper } from '../uploader/core/info-helper/input-info-helper';

import { fixOldBrowser, getSatErrorFromJqXHR } from './helper';
import {
    API_ERROR_BROKEN_SERVER_RESPONSE,
    API_ERROR_CONNECTION_PROBLEM,
    API_ERROR_INVALID_CONVERSION_OPTIONS,
    API_ERROR_JOB_CAN_NOT_BE_MODIFIED,
    API_ERROR_JOB_NOT_FOUND,
    API_ERROR_NOT_IMPLEMENTED,
    API_ERROR_PAYMENT_REQUIRED,
    API_ERROR_TOO_MANY_RETRIES,
    API_ERROR_JOB_CANCEL,
    API_ERROR_UNKNOWN, API_ERROR_JOB_CONVERSIONS_BLOCKED,
} from './consts';
import { satGlobals } from '../../qaamgo/sat/helper/sat-globals';
import { apiRequestManager } from './api-request-manager';
import { axiosWrapper } from '../../lib/axios-wrapper/axios-wrapper';
import {LimitData} from "../limits/new/limit-data";
import {useLimitsStore} from "../store/modules/limits-store";
import {TOAST_SHOW, TOASTS} from "../../qaamgo/modules/sat/components/toasts/toast-consts";
import {eventBus} from "../../lib/event-bus/event-bus";

function getResponseFromJqXHR(jqXHR) {
    let response = null;

    // this try only exists because i am not sure what happens if responseJSON does not exist
    try {
        response = jqXHR.responseText;

        if (jqXHR.responseJSON) {
            response = jqXHR.responseJSON;
        }
    } catch (e) {}

    return response;
}

/**
 * This is the API layer for the all the sat project
 *
 * @param {Api} api
 * @param {ApiConfig} config
 *
 * @constructor
 */
function SatApiHelper(api, config) {
    /** @type {Api} */
    this.api = api;

    /** @type {string} */
    this.baseUrlApi = satGlobals.getApiUrl(); // by default we grab the global api url

    if (testIsNonEmptyString(config.baseUrlApi)) {
        this.baseUrlApi = config.baseUrlApi;
    }
}

/**
 * @param {Object} [jobData]
 *
 * @return {JQuery.Deferred<RawJobInfo>}
 */
SatApiHelper.prototype.createJob = function (jobData) {
    /** @type {Api} */
    var api = this.api;

    if (!testIsObject(jobData)) {
        jobData = {};
    }

    var options = $.extend({}, api.baseAjaxOptions);

    options.url = this.baseUrlApi + '/api/jobs?async=true';
    options.data = jobData;

    var deferred = $.Deferred();

    /** @type {SatApiHelper} */
    var _this = this;

    api.ajaxWrapper
        .postJson(options)
        .done(function (serverResponse, textStatus, jqXHR) {
            serverResponse = fixOldBrowser(serverResponse);

            _this
                .getJobInfoUntilStatus(serverResponse.sat.id_job, 'incomplete', 10, true)
                .done(function (jobInfo) {
                    var jobInfoHelper = new JobInfoHelper(jobInfo);

                    if (!jobInfoHelper.isValid()) {
                        globalLogger.log('qgApiHelper createJob fail broken response', {
                            info: 'response: ' + JSON.stringify(serverResponse),
                        });

                        deferred.reject(API_ERROR_BROKEN_SERVER_RESPONSE);

                        return;
                    }

                    /** @type {RawJobInfo} */
                    var jobInfoRaw = jobInfo;

                    var jobId = jobInfoHelper.getId();

                    api.jobCache.setData(jobId, jobInfoRaw);

                    // add the job id to all further logs
                    globalLogger.addLogData('job_id', jobId);

                    globalLogger.log('qgApiHelper createJob done');

                    deferred.resolve(jobInfoRaw);
                })
                // API FAIL
                .fail(function (apiError, response) {
                    if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                        globalLogger.log('qgApiHelper createJob fail - payment required');

                        deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                        return;
                    }

                    deferred.reject(API_ERROR_CONNECTION_PROBLEM);
                });
        })
        // AJAX FAIL
        .fail(function (jqXHR, textStatus, errorThrown) {
            if (jqXHR.status === 402) {
                globalLogger.log('qgApiHelper createJob fail - payment required');

                deferred.reject(API_ERROR_PAYMENT_REQUIRED, getResponseFromJqXHR(jqXHR));

                return;
            }

            deferred.reject(API_ERROR_CONNECTION_PROBLEM);
        });

    return deferred;
};

/**
 * @param {string} jobId
 * @param async
 * @param sync
 *
 * @return {JQuery.Deferred<RawJobInfo>}
 */
SatApiHelper.prototype.getJobInfo = function (jobId, async = false, sync = false) {
    assertIsUUID4(jobId);

    /** @type {Api} */
    var api = this.api;

    var deferred = $.Deferred();

    var options = $.extend({}, api.baseAjaxOptions);

    options.url = this.baseUrlApi + '/api/jobs/' + jobId;

    if (async === true && sync === false) {
        options.url += '?async=true';
    }

    if (sync === true) {
        options.url += '?sync=true';
    }

    if (!api.numberOfCurrentJobInfoRequests.hasOwnProperty(jobId)) {
        api.numberOfCurrentJobInfoRequests[jobId] = 0;
    }

    api.numberOfCurrentJobInfoRequests[jobId]++;

    /** @type {SatApiHelper} */
    var _this = this;

    api.ajaxWrapper
        .get(options)
        .done(function (serverResponse, textStatus, jqXHR) {
            api.numberOfCurrentJobInfoRequests[jobId]--;

            serverResponse = fixOldBrowser(serverResponse);

            /** @type {RawJobInfo} */
            var jobInfoRaw = serverResponse;

            api.jobCache.setData(jobId, jobInfoRaw);

            deferred.resolve(jobInfoRaw);
        })
        // AJAX FAIL
        .fail(function (jqXHR, textStatus, errorThrown) {
            api.numberOfCurrentJobInfoRequests[jobId]--;

            if (jqXHR.status === 402) {
                globalLogger.log('qgApiHelper getJobInfo fail payment required', jqXHR.status);

                deferred.reject(API_ERROR_PAYMENT_REQUIRED, getResponseFromJqXHR(jqXHR));

                return;
            }

            if (jqXHR.status === 404) {
                globalLogger.log('qgApiHelper getJobInfo fail job not found', jqXHR.status);

                deferred.reject(API_ERROR_JOB_NOT_FOUND);

                return;
            }

            globalLogger.log('qgApiHelper getJobInfo fail connection problem', jqXHR.status);

            deferred.reject(API_ERROR_CONNECTION_PROBLEM);
        });

    return deferred;
};

/**
 * @param {string} jobId
 * @param {string}  status
 * @param {number} [retries=5]
 * @param {Boolean}  [async=true]
 *
 * @returns {JQuery.Deferred<RawJobInfo>}
 */
SatApiHelper.prototype.getJobInfoUntilStatus = function (jobId, status, retries, async) {
    assertIsUUID4(jobId);

    if (testIsUndefined(async)) {
        async = true;
    }

    if (!testIsNumber(retries)) {
        retries = 10;
    }

    assertIsNumber(retries);
    assertIsTrue(retries > 0);

    var deferred = $.Deferred();

    var getJobInfoRetries = 0;

    /** @type {SatApiHelper} */
    var _this = this;

    function getJobInfoHelper() {
        if (getJobInfoRetries > retries) {
            globalLogger.log('qgApiHelper getJobInfoUntilStatus fail - no more retries');

            deferred.reject(API_ERROR_TOO_MANY_RETRIES);

            return;
        }

        _this
            .getJobInfo(jobId, async)
            .done(function (jobInfo) {
                if (jobInfo.status.code === status) {
                    deferred.resolve(jobInfo);

                    return;
                }

                let timeout = 2000;
                if (jobInfo.status.code === 'init') {
                    timeout = 1000;
                }

                getJobInfoRetries++;

                setTimeout(getJobInfoHelper, timeout);
            })
            // API FAIL
            .fail(function (apiError, response) {
                if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                    globalLogger.log('qgApiHelper getJobInfoWithRetries getJobInfo fail - payment required', apiError);

                    deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                    return;
                }

                getJobInfoRetries++;

                setTimeout(getJobInfoHelper, 2000);
            });
    }

    getJobInfoHelper();

    return deferred;
};

/**
 * @param {string} jobId
 * @param {number} [retries=5]
 *
 * @returns {JQuery.Deferred<RawJobInfo>}
 */
SatApiHelper.prototype.getJobInfoWithRetries = function (jobId, retries) {
    assertIsUUID4(jobId);

    if (!testIsNumber(retries)) {
        retries = 5;
    }

    assertIsNumber(retries);
    assertIsTrue(retries > 0);

    var deferred = $.Deferred();

    var getJobInfoRetries = 0;

    /** @type {SatApiHelper} */
    var _this = this;

    function getJobInfoHelper() {
        if (getJobInfoRetries > retries) {
            globalLogger.log('qgApiHelper getJobInfoWithRetries fail - no more retries');

            deferred.reject(API_ERROR_TOO_MANY_RETRIES);

            return;
        }

        _this
            .getJobInfo(jobId)
            .done(function (jobInfo) {
                deferred.resolve(jobInfo);
            })
            // API FAIL
            .fail(function (apiError, response) {
                if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                    globalLogger.log('qgApiHelper getJobInfoWithRetries getJobInfo fail - payment required');

                    deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                    return;
                }

                getJobInfoRetries++;

                setTimeout(getJobInfoHelper, 2000);
            });
    }

    getJobInfoHelper();

    return deferred;
};

/**
 * @param {string} jobId
 * @param {object} inputData
 *
 * @return {JQuery.Deferred<RawInputInfo>}
 */
SatApiHelper.prototype.addInput = function (jobId, inputData) {
    assertIsUUID4(jobId);
    assertIsObject(inputData);
    assertIsNotArray(inputData);
    assertObjectHasKey(inputData, 'type');

    /** @type {Api} */
    var api = this.api;

    var deferred = $.Deferred();

    var options = $.extend({}, api.baseAjaxOptions);

    options.url = this.baseUrlApi + '/api/jobs/' + jobId + '/input';
    options.data = inputData;

    /** @type {SatApiHelper} */
    var _this = this;

    api.ajaxWrapper
        .postJson(options)
        .done(function (serverResponse, textStatus, jqXHR) {
            serverResponse = fixOldBrowser(serverResponse);

            /** @type {RawInputInfo} */
            var rawInputInfo = serverResponse;

            globalLogger.log('qgApiHelper addJobInput done', JSON.stringify(rawInputInfo));
            deferred.resolve(rawInputInfo);
        })
        // AJAX FAIL
        .fail(function (jqXHR, textStatus, errorThrown) {
            if (jqXHR.status === 0) {
                globalLogger.log('qgApiHelper addJobInput fail connection problem', jqXHR.status);

                deferred.reject(API_ERROR_CONNECTION_PROBLEM);

                return;
            }

            if (jqXHR.status === 402) {
                globalLogger.log('qgApiHelper addJobInput fail payment required');

                deferred.reject(API_ERROR_PAYMENT_REQUIRED, getResponseFromJqXHR(jqXHR));

                return;
            }

            globalLogger.log('qgApiHelper addJobInput fail connection problem', jqXHR.status);

            deferred.reject(API_ERROR_UNKNOWN);
        });

    return deferred;
};

/**
 * @param {string} jobId
 * @param {object} inputData
 * @param {number} numberOfInputsInJob
 *
 * @return {JQuery.Deferred<RawInputInfo>}
 */
SatApiHelper.prototype.addInputWithCheck = function (jobId, inputData, numberOfInputsInJob) {
    assertIsUUID4(jobId);
    assertIsObject(inputData);
    assertIsNotArray(inputData);
    assertObjectHasKey(inputData, 'type');
    assertIsNumber(numberOfInputsInJob);
    assertIsTrue(numberOfInputsInJob >= 0);

    var deferred = $.Deferred();

    var addInputRetries = 0;

    /** @type {SatApiHelper} */
    var _this = this;

    function checkJobForInputInCaseOfConnectionProblem() {
        _this
            .getJobInfoWithRetries(jobId, 3)
            .done(function (jobInfo) {
                /** @type {RawJobInfo} */
                var job = jobInfo;

                var n = jobInfo.input.length;

                // input was not added
                if (n === numberOfInputsInJob) {
                    globalLogger.log('qgApiHelper addInputWithCheck retry');

                    // try to add it again
                    setTimeout(addInputHelper, 2000);

                    return;
                }

                // input was added
                if (n === numberOfInputsInJob + 1) {
                    globalLogger.log('qgApiHelper addInputWithCheck done - with retry');

                    deferred.resolve(jobInfo.input[n - 1]);

                    return;
                }

                globalLogger.log('qgApiHelper addInputWithCheck fail', 'wrong number of inputs');

                // this should never happen
                deferred.reject(API_ERROR_UNKNOWN);
            })
            // API FAIL
            .fail(function (apiError, response) {
                if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                    globalLogger.log('qgApiHelper addInputWithCheck fail - payment required');

                    deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                    return;
                }

                globalLogger.log('qgApiHelper addInputWithCheck fail', apiError);

                deferred.reject(API_ERROR_UNKNOWN);
            });
    }

    function addInputHelper() {
        if (addInputRetries > 5) {
            globalLogger.log('qgApiHelper addInputWithCheck fail - too many retries');

            deferred.reject(API_ERROR_TOO_MANY_RETRIES);

            return;
        }

        _this
            .addInput(jobId, inputData)
            .done(function (input) {
                _this
                    .getJobInfoUntilInputCount(jobId, numberOfInputsInJob + 1)
                    .done(function () {
                        deferred.resolve(input);
                    })
                    // API FAIL
                    .fail(function (apiError, response) {
                        if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                            globalLogger.log('qgApiHelper addInputWithCheck fail - payment required');

                            deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                            return;
                        }

                        globalLogger.log('qgApiHelper addInputWithCheck fail', apiError);

                        deferred.reject(API_ERROR_UNKNOWN);
                    });
            })
            // API FAIL
            .fail(function (apiError, response) {
                // if there was a connection problem or a time out then we need to
                // check if the input arrived in the API
                if (apiError === API_ERROR_CONNECTION_PROBLEM) {
                    checkJobForInputInCaseOfConnectionProblem();

                    return;
                }

                if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                    globalLogger.log('qgApiHelper addInputWithCheck fail - payment required');

                    deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                    return;
                }

                globalLogger.log('qgApiHelper addInputWithCheck fail');

                deferred.reject(apiError);
            });

        addInputRetries++;
    }

    addInputHelper();

    return deferred;
};

/**
 * @param {string} jobId
 * @param {object} inputsData
 *
 * @return {JQuery.Deferred}
 */
SatApiHelper.prototype.addInputs = function (jobId, inputsData) {
    assertIsUUID4(jobId);
    assertIsObject(inputsData);

    /** @type {Api} */
    var api = this.api;

    var deferred = $.Deferred();

    var options = $.extend({}, api.baseAjaxOptions);

    options.url = this.baseUrlApi + '/api/jobs/' + jobId + '/input';
    options.data = inputsData;

    /** @type {SatApiHelper} */
    const _this = this;

    api.ajaxWrapper
        .postJson(options)
        .done(function (serverResponse, textStatus, jqXHR) {
            serverResponse = fixOldBrowser(serverResponse);

            // this is the whole server response with all the inputs
            // it is either a single input object or an array of input objects
            var rawInputInfo = serverResponse;

            // the following lines check if the data is valid
            var testInput;

            // if there is more than one input, then only the first is checked
            if (testIsArray(rawInputInfo)) {
                testInput = rawInputInfo[0];
            } else {
                testInput = rawInputInfo;
            }

            var testInputInfoHelper = new InputInfoHelper(testInput);

            if (!testInputInfoHelper.isValid()) {
                globalLogger.log('qgApiHelper addJobInput fail broken response', {
                    info: 'response: ' + JSON.stringify(rawInputInfo),
                });

                deferred.reject(API_ERROR_BROKEN_SERVER_RESPONSE);

                return;
            }

            globalLogger.log('qgApiHelper addJobInput done', JSON.stringify(inputsData));

            deferred.resolve(rawInputInfo);
        })
        // AJAX FAIL
        .fail(function (jqXHR, textStatus, errorThrown) {
            if (jqXHR.status === 402) {
                globalLogger.log('qgApiHelper addJobInput fail payment required');

                deferred.reject(API_ERROR_PAYMENT_REQUIRED, getResponseFromJqXHR(jqXHR));

                return;
            }

            globalLogger.log('qgApiHelper addJobInput fail connection problem', jqXHR.status);

            deferred.reject(API_ERROR_CONNECTION_PROBLEM);
        });

    return deferred;
};

/**
 * @param {string} jobId
 * @param {object} conversion
 *
 * @return {JQuery.Deferred}
 */
SatApiHelper.prototype.addConversion = function (jobId, conversion) {
    assertIsUUID4(jobId);
    assertIsObject(conversion);

    /** @type {Api} */
    var api = this.api;

    var deferred = $.Deferred();

    var options = $.extend({}, api.baseAjaxOptions);

    // addConversionAction() in src/Converter/Controller/Api/ConversionController.php
    options.url = this.baseUrlApi + '/api/jobs/' + jobId + '/conversions';
    options.data = conversion;

    /** @type {SatApiHelper} */
    var _this = this;

    api.ajaxWrapper
        .postJson(options)
        .done(function (serverResponse, textStatus, jqXHR) {
            serverResponse = fixOldBrowser(serverResponse);

            globalLogger.log('qgApiHelper addConversion done', JSON.stringify(conversion));

            // NOTE: due to the async nature of the sat-client this response is most likely an empty array
            deferred.resolve(serverResponse);
        })
        // AJAX FAIL
        .fail(function (jqXHR, textStatus, errorThrown) {
            var satError = getSatErrorFromJqXHR(jqXHR);
            if (satError) {
                if (satError.code === 2) {
                    globalLogger.log('qgApiHelper addConversion fail - invalid conversion option', {
                        http_status: jqXHR.status,
                        info: 'conversion options: ' + JSON.stringify(options),
                    });

                    deferred.reject(API_ERROR_INVALID_CONVERSION_OPTIONS);

                    return;
                }

                if (satError.code === 2002) {
                    globalLogger.log('qgApiHelper addConversion fail - job can not be modified', {
                        http_status: jqXHR.status,
                        info: 'conversion options: ' + JSON.stringify(options),
                    });

                    deferred.reject(API_ERROR_JOB_CAN_NOT_BE_MODIFIED);

                    return;
                }

                if (satError.code === 666) {
                    globalLogger.log('qgApiHelper addConversion fail - job blocked conversion', {
                        http_status: jqXHR.status,
                        info: 'conversion options: ' + JSON.stringify(options),
                    });

                    let limitsList = [];
                    const limitsStore = useLimitsStore();

                    limitsList.push(LimitData.createFromConstraintStatus({
                        hit: true,
                        ident: satError.code,
                        type : 'limit_job_conversions_blocked',
                        ttl : satError.ttl
                    }));
                    limitsStore.addLimitsRefresh(limitsList);
                    eventBus.emit(TOAST_SHOW, {
                        name: TOASTS.TOAST_RESULT_LIMITS,
                        data: null,
                    });

                    return;
                }
            }

            if (jqXHR.status === 0) {
                globalLogger.log('qgApiHelper addConversion fail - connection problem', jqXHR.status);

                deferred.reject(API_ERROR_CONNECTION_PROBLEM);

                return;
            }

            if (jqXHR.status === 402) {
                globalLogger.log('qgApiHelper addConversion fail - payment required');

                deferred.reject(API_ERROR_PAYMENT_REQUIRED, getResponseFromJqXHR(jqXHR));

                return;
            }

            globalLogger.log('qgApiHelper addConversion fail - unknown', jqXHR.status);

            deferred.reject(API_ERROR_UNKNOWN);
        });

    return deferred;
};

/**
 * This is the clever function to add a conversion to an empty job. If adding the
 * conversion is failing somehow then it retries in some cases until exactly one conversion
 * is in hte job
 *
 * @param {string} jobId
 * @param {object} conversion
 *
 * @return {JQuery.Deferred}
 */
SatApiHelper.prototype.addConversionWithCheck = function (jobId, conversion) {
    assertIsUUID4(jobId);

    var deferred = $.Deferred();

    /** @type {SatApiHelper} */
    var _this = this;

    var addConversionRetries = 0;

    function checkJobForConversion() {
        if (addConversionRetries > 5) {
            globalLogger.log('qgApiHelper addConversionWithCheck fail - too many retries');

            deferred.reject(API_ERROR_TOO_MANY_RETRIES);

            return;
        }
        _this
            .getJobInfoWithRetries(jobId, 5)
            .done(function (jobInfo) {
                if (testIsArray(jobInfo.conversion)) {
                    if (jobInfo.conversion.length === 0) {
                        globalLogger.log('qgApiHelper addConversionWithCheck retry - no conversion');

                        setTimeout(checkJobForConversion, 1000);

                        return;
                    }

                    if (jobInfo.conversion.length === 1) {
                        globalLogger.log('qgApiHelper addConversionWithCheck done - after fail');

                        deferred.resolve(jobInfo.conversion[0]);

                        return;
                    }

                    globalLogger.log('qgApiHelper addConversionWithCheck fail - too many conversions');

                    deferred.reject(API_ERROR_UNKNOWN);
                }
            })
            // API FAIL
            .fail(function (apiError, response) {
                if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                    globalLogger.log('qgApiHelper addConversionWithCheck fail - payment required');

                    deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                    return;
                }

                globalLogger.log('qgApiHelper addConversionWithCheck fail - fail in getjobinfo');

                deferred.reject(API_ERROR_UNKNOWN);
            });
        addConversionRetries++;
    }

    function addConversionHelper() {
        if (addConversionRetries > 5) {
            globalLogger.log('qgApiHelper addConversionWithCheck fail - too many retries');

            deferred.reject(API_ERROR_TOO_MANY_RETRIES);

            return;
        }

        _this
            .addConversion(jobId, conversion)
            .done(function (conversion) {
                // NOTE: due to the async nature of the sat-client this response is most likely empty
                //deferred.resolve(conversion);
                checkJobForConversion()
            })
            // API FAIL
            .fail(function (apiError, response) {
                if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                    globalLogger.log('qgApiHelper addConversionWithCheck fail - payment required');

                    deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                    return;
                }

                // only these api errors should be retried
                var shouldBeRetried = apiError === API_ERROR_CONNECTION_PROBLEM || apiError === API_ERROR_UNKNOWN;

                if (shouldBeRetried) {
                    addConversionHelper();

                    return;
                }

                deferred.reject(apiError);
            });
        addConversionRetries++;
    }

    addConversionHelper();

    return deferred;
};

/**
 * @param {string} jobId
 * @param {string} serializedForm
 *
 * @return {JQuery.Deferred}
 */
SatApiHelper.prototype.startJob = function (jobId, serializedForm) {
    assertIsUUID4(jobId);

    var deferred = $.Deferred();

    deferred.reject(API_ERROR_NOT_IMPLEMENTED);

    return deferred;
};

/**
 * @param {string} jobId
 * @param {string} inputId
 *
 * @return {JQuery.Deferred}
 */
SatApiHelper.prototype.deleteInput = function (jobId, inputId) {
    assertIsUUID4(jobId);
    assertIsUUID4(inputId);

    /** @type {Api} */
    var api = this.api;

    var deferred = $.Deferred();

    var options = $.extend({}, api.baseAjaxOptions);

    options.url = this.baseUrlApi + '/api/jobs/' + jobId + '/input/' + inputId + '/delete';
    options.data = $.extend({}, api.baseData);

    const _this = this;

    api.ajaxWrapper
        .postJson(options)
        .done(function (serverResponse, textStatus, jqXHR) {
            serverResponse = fixOldBrowser(serverResponse);

            globalLogger.log('qgApiHelper deleteInput done', inputId);

            deferred.resolve(serverResponse);
        })
        // AJAX FAIL
        .fail(function (jqXHR, textStatus, errorThrown) {
            if (jqXHR.status === 402) {
                globalLogger.log('qgApiHelper deleteInput fail payment required', jqXHR.status);

                deferred.reject(API_ERROR_PAYMENT_REQUIRED, getResponseFromJqXHR(jqXHR));

                return;
            }

            globalLogger.log('qgApiHelper deleteInput fail connection problem', jqXHR.status);

            deferred.reject(API_ERROR_CONNECTION_PROBLEM);
        });

    return deferred;
};

/**
 * @param {string} jobId
 * @param {string} inputId
 *
 * @return {JQuery.Deferred}
 */
SatApiHelper.prototype.cancelJob = function (jobId) {
    assertIsUUID4(jobId);

    /** @type {Api} */
    var api = this.api;

    var deferred = $.Deferred();

    var options = $.extend({}, api.baseAjaxOptions);

    options.url = this.baseUrlApi + '/api/jobs/' + jobId + '/cancel';

    const _this = this;

    api.ajaxWrapper
        .postJson(options)
        .done(function (serverResponse, textStatus, jqXHR) {
            serverResponse = fixOldBrowser(serverResponse);

            globalLogger.log('qgApiHelper cancelJob done', jobId);

            deferred.resolve(serverResponse);
        })
        // AJAX FAIL
        .fail(function (jqXHR, textStatus, errorThrown) {
            globalLogger.log('qgApiHelper cancelJob fail', jqXHR.status);

            deferred.reject(API_ERROR_JOB_CANCEL, getResponseFromJqXHR(jqXHR));
        });

    return deferred;
};

/**
 * @param {string} jobId
 * @param {string} inputId
 *
 * @return {JQuery.Deferred}
 */
SatApiHelper.prototype.deleteInputAndWait = function (jobId, inputId) {
    assertIsUUID4(jobId);
    assertIsUUID4(inputId);

    /** @type {Api} */
    var api = this.api;

    var deferred = $.Deferred();

    this.deleteInput(jobId, inputId)
        .done(() => {
            this.getJobInfoUntilInputIsDeleted(jobId, inputId)
                .done(() => {
                    deferred.resolve();
                })
                .fail((apiError, response) => {
                    if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                        globalLogger.log('qgApiHelper delete input and wait fail - payment required');

                        deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                        return;
                    }

                    globalLogger.log('qgApiHelper delete input and wait fail connection problem', apiError);

                    deferred.reject(API_ERROR_CONNECTION_PROBLEM);
                });
        })
        .fail((apiError, response) => {
            globalLogger.log('qgApiHelper delete input and wait fail connection problem', apiError);

            if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                globalLogger.log('qgApiHelper addConversionWithCheck fail - payment required');

                deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                return;
            }

            deferred.reject(API_ERROR_CONNECTION_PROBLEM);
        });

    return deferred;
};

/**
 * @param {string} jobId
 * @param {string} inputId
 * @param {string} password
 *
 * @return {JQuery.Deferred}
 */
SatApiHelper.prototype.setPassword = function (jobId, inputId, password) {
    assertIsUUID4(jobId);
    assertIsUUID4(inputId);
    var deferred = $.Deferred();

    /** @type {Api} */
    var api = this.api;

    /** @type {SatApiHelper} */
    var _this = this;

    var modifiedAt = 0;

    var data = $.extend({}, api.baseData, {
        credentials: {
            decrypt_password: password,
        },
    });

    var options = $.extend({}, api.baseAjaxOptions);

    options.url = this.baseUrlApi + '/api/jobs/' + jobId + '/input/' + inputId + '/patch';
    options.data = data;

    function setPasswordApi() {
        api.ajaxWrapper
            .postJson(options)
            .done(function (serverResponse, textStatus, jqXHR) {
                serverResponse = fixOldBrowser(serverResponse);

                globalLogger.log('qgApiHelper set password done', inputId);
                _this
                    .getUpdatedInput(jobId, inputId, modifiedAt)
                    .done(function (inputInfo) {
                        globalLogger.log('qgApiHelper set password finished', inputId);

                        deferred.resolve(inputInfo);
                    })
                    // API FAIL
                    .fail(function (apiError, response) {
                        if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                            globalLogger.log('qgApiHelper set password fail  payment required');

                            deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                            return;
                        }

                        globalLogger.log('qgApiHelper set password fail connection problem', apiError);

                        deferred.reject(API_ERROR_CONNECTION_PROBLEM);
                    });
            })
            // AJAX FAIL
            .fail(function (jqXHR, textStatus, errorThrown) {
                if (jqXHR.status === 402) {
                    globalLogger.log('qgApiHelper set password fail payment required', jqXHR.status);

                    deferred.reject(API_ERROR_PAYMENT_REQUIRED, getResponseFromJqXHR(jqXHR));

                    return;
                }

                globalLogger.log('qgApiHelper set password fail connection problem', jqXHR.status);

                deferred.reject(API_ERROR_CONNECTION_PROBLEM);
            });
    }

    this.getJobInfoUntilInput(jobId, inputId)
        .done(function (input) {
            modifiedAt = input.modified_at;
            setPasswordApi();
        })
        // API FAIL
        .fail(function (apiError, response) {
            if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                globalLogger.log('qgApiHelper set password fail  payment required');

                deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                return;
            }

            globalLogger.log('qgApiHelper set password fail connection problem', apiError);

            deferred.reject(API_ERROR_CONNECTION_PROBLEM);
        });

    return deferred;
};

/**
 * @param {string} jobId
 * @param {string} inputId
 */
SatApiHelper.prototype.getJobInfoUntilInput = function (jobId, inputId) {
    var deferred = $.Deferred();
    /** @type {SatApiHelper} */
    var _this = this;

    const maxRetries = 60;
    let retries = 0;

    function getJobInfoHelper() {
        _this
            .getJobInfo(jobId)
            .done(function (response) {
                let foundInput = response.input.find(function (element, index, array) {
                    return element.id === inputId;
                });

                if (typeof foundInput !== 'undefined') {
                    deferred.resolve(foundInput);

                    return;
                }

                if (retries > maxRetries) {
                    deferred.reject(API_ERROR_TOO_MANY_RETRIES);
                }

                ++retries;

                setTimeout(getJobInfoHelper, 1000);
            })
            // API FAIL
            .fail(function (apiError, response) {
                if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                    globalLogger.log('qgApiHelper getJobInfoUntilInput fail  payment required');

                    deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                    return;
                }

                globalLogger.log('qgApiHelper getJobInfoUntilInput fail connection problem', apiError);

                deferred.reject(API_ERROR_CONNECTION_PROBLEM);
            });
    }

    getJobInfoHelper();

    return deferred;
};

/**
 * @param {string} jobId
 * @param {string} inputId
 */
SatApiHelper.prototype.getJobInfoUntilInputIsDeleted = function (jobId, inputId) {
    var deferred = $.Deferred();
    /** @type {SatApiHelper} */
    var _this = this;

    const maxRetries = 60;
    let retries = 0;

    function getJobInfoHelper() {
        _this
            .getJobInfo(jobId)
            .done(function (response) {
                let foundInput = response.input.find(function (element, index, array) {
                    return element.id === inputId;
                });

                if (typeof foundInput === 'undefined') {
                    deferred.resolve();

                    return;
                }

                if (retries > maxRetries) {
                    deferred.reject(API_ERROR_TOO_MANY_RETRIES);
                }

                ++retries;

                setTimeout(getJobInfoHelper, 1000);
            })
            .fail(function (apiError, response) {
                globalLogger.log('qgApiHelper getJobInfoUntilInputIsDeleted connection problem', apiError);

                deferred.reject(API_ERROR_CONNECTION_PROBLEM);
            });
    }

    getJobInfoHelper();

    return deferred;
};

/**
 * @param {string} jobId
 * @param {int} inputCount
 */
SatApiHelper.prototype.getJobInfoUntilInputCount = function (jobId, inputCount) {
    var deferred = $.Deferred();
    /** @type {SatApiHelper} */
    var _this = this;

    const maxRetries = 60;
    let retries = 0;

    const connectionMaxRetries = 30;
    let connectionRetries = 0;

    function getJobInfoHelper() {
        _this
            .getJobInfo(jobId)
            .done(function (response) {
                connectionRetries = 0;

                if (response.input.length >= inputCount) {
                    deferred.resolve(response);

                    return;
                }

                if (retries > maxRetries) {
                    deferred.reject(API_ERROR_TOO_MANY_RETRIES);

                    return;
                }

                ++retries;

                setTimeout(getJobInfoHelper, 1000);
            })
            // API FAIL
            .fail(function (apiError, response) {
                if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                    globalLogger.log('qgApiHelper getJobInfoUntilInputCount fail payment required');

                    deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                    return;
                }

                if (connectionRetries < connectionMaxRetries) {
                    connectionRetries++;

                    setTimeout(getJobInfoHelper, 3000);

                    return;
                }

                globalLogger.log('qgApiHelper getJobInfoUntilInputCount fail', apiError);

                deferred.reject(API_ERROR_CONNECTION_PROBLEM);
            });
    }

    getJobInfoHelper();

    return deferred;
};

/**
 * @param {string} jobId
 */
SatApiHelper.prototype.getJobInfoUntilAtLeastOneInput = function (jobId) {
    var deferred = $.Deferred();
    /** @type {SatApiHelper} */
    var _this = this;

    const maxRetries = 60;
    let retries = 0;

    function getJobInfoHelper() {
        _this
            .getJobInfo(jobId)
            .done(function (response) {
                if (response.input.length >= 1) {
                    deferred.resolve(response);

                    return;
                }

                if (retries > maxRetries) {
                    deferred.reject(API_ERROR_TOO_MANY_RETRIES);

                    return;
                }

                ++retries;

                setTimeout(getJobInfoHelper, 1000);
            })
            // API FAIL
            .fail(function (apiError, response) {
                if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                    globalLogger.log('qgApiHelper getJobInfoUntilAtLeastOneInput fail payment required');

                    deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                    return;
                }

                globalLogger.log('qgApiHelper getJobInfoUntilAtLeastOneInput fail', apiError);

                deferred.reject(API_ERROR_CONNECTION_PROBLEM);
            });
    }

    getJobInfoHelper();

    return deferred;
};

/**
 * @param {string} jobId
 * @param {string} inputId
 * @param {int} modifiedAt
 */
SatApiHelper.prototype.getUpdatedInput = function (jobId, inputId, modifiedAt) {
    let deferred = $.Deferred();

    /** @type {SatApiHelper} */
    let _this = this;

    const maxRetries = 60;
    let retries = 0;

    function getUpdatedInfo() {
        _this
            .getJobInfo(jobId)
            .done(function (response) {
                let foundInput = response.input.find(function (element, index, array) {
                    return element.id === inputId;
                });

                if (foundInput.modified_at > modifiedAt) {
                    deferred.resolve(foundInput);

                    return;
                }

                if (retries > maxRetries) {
                    deferred.reject(API_ERROR_TOO_MANY_RETRIES);

                    return;
                }

                ++retries;

                setTimeout(getUpdatedInfo, 1000);
            })
            // API FAIL
            .fail(function (apiError, response) {
                if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                    globalLogger.log('qgApiHelper get updated input fail payment required');

                    deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                    return;
                }

                globalLogger.log('qgApiHelper get updated input fail connection problem', apiError);

                deferred.reject(API_ERROR_CONNECTION_PROBLEM);
            });
    }

    getUpdatedInfo();

    return deferred;
};

SatApiHelper.prototype.createJobPromise = async function (jobData) {
    return new Promise(async (resolve, reject) => {
        const api = this.api;

        try {
            const response = await apiRequestManager.post('/api/jobs?async=true', jobData);

            const jobInfo = await this.getJobInfoUntilStatusPromise(response.data.sat.id_job, 'incomplete', 10, true);
            const jobInfoHelper = new JobInfoHelper(jobInfo);

            if (!jobInfoHelper.isValid()) {
                globalLogger.log('qgApiHelper createJobPromise fail broken response', {
                    info: 'response: ' + JSON.stringify(response.data),
                });

                reject(API_ERROR_BROKEN_SERVER_RESPONSE);

                return;
            }

            /** @type {RawJobInfo} */
            var jobInfoRaw = jobInfo;

            var jobId = jobInfoHelper.getId();

            api.jobCache.setData(jobId, jobInfoRaw);

            // add the job id to all further logs
            globalLogger.addLogData('job_id', jobId);

            globalLogger.log('qgApiHelper createJobPromise done');

            resolve(jobInfoRaw);
        } catch (e) {
            if (e.response.status === 402) {
                globalLogger.log('qgApiHelper createJobPromise fail - payment required');

                reject({ message: API_ERROR_PAYMENT_REQUIRED, responseData: e.response.data });

                return;
            }

            reject(API_ERROR_CONNECTION_PROBLEM);
        }
    });
};

/**
 *
 * @param {String} jobId
 * @param {Boolean} async
 * @param {Boolean} sync
 * @return {Promise<RawJobInfo>}
 */
SatApiHelper.prototype.getJobInfoPromise = async function (jobId, async = false, sync = false) {
    return new Promise(async (resolve, reject) => {
        assertIsUUID4(jobId);

        /** @type {Api} */
        var api = this.api;

        let url = `/api/jobs/${jobId}`;

        if (async === true && sync === false) {
            url += '?async=true';
        }

        if (sync === true) {
            url += '?sync=true';
        }

        if (!api.numberOfCurrentJobInfoRequests.hasOwnProperty(jobId)) {
            api.numberOfCurrentJobInfoRequests[jobId] = 0;
        }

        api.numberOfCurrentJobInfoRequests[jobId]++;

        /** @type {SatApiHelper} */
        try {
            const response = await apiRequestManager.get(url);

            api.numberOfCurrentJobInfoRequests[jobId]--;

            /** @type {RawJobInfo} */
            var jobInfoRaw = response.data;

            api.jobCache.setData(jobId, jobInfoRaw);
            resolve(jobInfoRaw);
        } catch (e) {
            api.numberOfCurrentJobInfoRequests[jobId]--;

            if (e.response.status === 402) {
                globalLogger.log('qgApiHelper getJobInfoPromise fail payment required', e.response.data);

                reject({ message: API_ERROR_PAYMENT_REQUIRED, responseData: e.response.data });
                return;
            }

            if (e.response.status === 404) {
                globalLogger.log('qgApiHelper getJobInfoPromise fail job not found', e.response.data);

                reject({ message: API_ERROR_JOB_NOT_FOUND, responseData: e.response.data });
                return;
            }

            globalLogger.log('qgApiHelper getJobInfoPromise fail connection problem', e.response.data);

            reject({ message: API_ERROR_CONNECTION_PROBLEM, responseData: e.response.data });
        }
    });
};

SatApiHelper.prototype.getJobInfoUntilStatusPromise = async function (jobId, status, retries, async) {
    return new Promise(async (resolve, reject) => {
        if (testIsUndefined(async)) {
            async = true;
        }

        if (!testIsNumber(retries)) {
            retries = 5;
        }

        assertIsNumber(retries);
        assertIsTrue(retries > 0);

        var getJobInfoRetries = 0;

        const _this = this;

        async function getJobInfoHelper() {
            if (getJobInfoRetries > retries) {
                globalLogger.log('qgApiHelper getJobInfoUntilStatusPromise fail - no more retries');

                reject(API_ERROR_TOO_MANY_RETRIES);

                return;
            }

            try {
                let jobInfo = await _this.getJobInfoPromise(jobId, async);

                if (jobInfo.status.code === status) {
                    resolve(jobInfo);
                    return;
                }

                let timeout = 2000;
                if (jobInfo.status.code === 'init') {
                    timeout = 200;
                }

                getJobInfoRetries++;

                setTimeout(getJobInfoHelper, timeout);
            } catch (e) {
                if (e?.message === API_ERROR_JOB_NOT_FOUND) {
                    reject({ message: API_ERROR_JOB_NOT_FOUND });
                    return;
                }

                if (e?.response?.status === 402) {
                    globalLogger.log(
                        'qgApiHelper getJobInfoWithRetries getJobInfoUntilStatusPromise fail - payment required',
                        e.response.data
                    );

                    reject({ message: API_ERROR_PAYMENT_REQUIRED, responseData: e.response.data });

                    return;
                }

                getJobInfoRetries++;

                setTimeout(getJobInfoHelper, 2000);
            }
        }

        try {
            await getJobInfoHelper();
        } catch (e) {
            if (e.response.status === 402) {
                globalLogger.log(
                    'qgApiHelper getJobInfoWithRetries getJobInfoUntilStatusPromise fail - payment required',
                    e.response.data
                );

                reject({ message: API_ERROR_PAYMENT_REQUIRED, responseData: e.response.data });
            }

            if (e.response.status === 404) {
                reject({ message: API_ERROR_JOB_NOT_FOUND });
            }
        }
    });
};

SatApiHelper.prototype.addConversionWithCheckPromise = function (jobId, conversion) {
    return new Promise(async (resolve, reject) => {
        assertIsUUID4(jobId);

        /** @type {SatApiHelper} */
        var _this = this;

        var addConversionRetries = 0;

        async function checkJobForConversion() {
            const jobInfo = await _this.getJobInfoWithRetriesPromise(jobId, 5);
            try {
                if (testIsArray(jobInfo.conversion)) {
                    if (jobInfo.conversion.length === 0) {
                        globalLogger.log('qgApiHelper addConversionWithCheckPromise retry - no conversion');

                        setTimeout(addConversionHelper, 2000);

                        return;
                    }

                    if (jobInfo.conversion.length === 1) {
                        globalLogger.log('qgApiHelper addConversionWithCheckPromise done - after fail');

                        resolve(jobInfo.conversion[0]);

                        return;
                    }

                    globalLogger.log('qgApiHelper addConversionWithCheckPromise fail - too many conversions');

                    reject(API_ERROR_UNKNOWN);
                }
            } catch (e) {
                if (e.response.status === 402) {
                    globalLogger.log('qgApiHelper addConversionWithCheckPromise fail - payment required');

                    reject({ message: API_ERROR_PAYMENT_REQUIRED, responseData: e.response.data });

                    return;
                }

                globalLogger.log('qgApiHelper addConversionWithCheckPromise fail - fail in getjobinfo');

                reject({ message: API_ERROR_UNKNOWN, responseData: e.response.data });
            }
        }

        async function addConversionHelper() {
            if (addConversionRetries > 5) {
                globalLogger.log('qgApiHelper addConversionWithCheckPromise fail - too many retries');

                reject(API_ERROR_TOO_MANY_RETRIES);

                return;
            }
            try {
                const result = await _this.addConversionPromise(jobId, conversion);
                resolve(result);
            } catch (e) {
                if (e.response.status === 404) {
                    globalLogger.log('qgApiHelper addConversionWithCheckPromise fail - payment required');

                    reject({ message: API_ERROR_PAYMENT_REQUIRED, responseData: e.response.data });

                    return;
                }

                //TODO make this work somehow
                // only these api errors should be retried
                var shouldBeRetried = e === API_ERROR_CONNECTION_PROBLEM || e === API_ERROR_UNKNOWN;

                if (shouldBeRetried) {
                    await checkJobForConversion();

                    return;
                }

                reject(e.message);
            }

            addConversionRetries++;
        }

        await addConversionHelper();
    });
};

SatApiHelper.prototype.getJobInfoWithRetriesPromise = async function (jobId, retries) {
    return new Promise((resolve, reject) => {
        assertIsUUID4(jobId);

        if (!testIsNumber(retries)) {
            retries = 5;
        }

        assertIsNumber(retries);
        assertIsTrue(retries > 0);

        var getJobInfoRetries = 0;

        /** @type {SatApiHelper} */
        var _this = this;

        async function getJobInfoHelper() {
            if (getJobInfoRetries > retries) {
                globalLogger.log('qgApiHelper getJobInfoWithRetriesPromise fail - no more retries');

                reject(API_ERROR_TOO_MANY_RETRIES);

                return;
            }

            try {
                const jobInfo = await _this.getJobInfoPromise(jobId);
                resolve(jobInfo);
            } catch (e) {
                if (e.response.status === 402) {
                    globalLogger.log('qgApiHelper getJobInfoWithRetriesPromise getJobInfo fail - payment required');

                    reject({ message: API_ERROR_PAYMENT_REQUIRED, responseData: e.response.data });

                    return;
                }
            }

            getJobInfoRetries++;

            setTimeout(getJobInfoHelper, 2000);
        }

        getJobInfoHelper();
    });
};

SatApiHelper.prototype.addConversionPromise = async function (jobId, conversion) {
    return new Promise(async (resolve, reject) => {
        assertIsUUID4(jobId);
        assertIsObject(conversion);

        /** @type {Api} */
        var api = this.api;

        // addConversionAction() in src/Converter/Controller/Api/ConversionController.php
        const url = this.baseUrlApi + '/api/jobs/' + jobId + '/conversions';
        const data = conversion;

        /** @type {SatApiHelper} */
        var _this = this;

        /**
         * @type {AxiosConfig}
         */
        const config = {
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Methods': 'GET, POST, PATCH, PUT, DELETE, OPTIONS',
                'Access-Control-Allow-Headers': 'Origin, Content-Type, X-Auth-Token',
                'Access-Control-Allow-Credentials': true,
            },
            withCredentials: false,
        };

        try {
            const response = await axiosWrapper.post(url, data, config);

            globalLogger.log('qgApiHelper addConversionPromise done', JSON.stringify(conversion));

            resolve(response.data);
        } catch (e) {
            //TODO idk how this works

            // var satError = getSatErrorFromJqXHR(jqXHR);
            // if (satError) {
            //     if (satError.code === 2) {
            //         globalLogger.log('qgApiHelper addConversion fail - invalid conversion option', {
            //             http_status: jqXHR.status,
            //             info: 'conversion options: ' + JSON.stringify(options),
            //         });
            //
            //         deferred.reject(API_ERROR_INVALID_CONVERSION_OPTIONS);
            //
            //         return;
            //     }
            //
            //     if (satError.code === 2002) {
            //         globalLogger.log('qgApiHelper addConversion fail - job can not be modified', {
            //             http_status: jqXHR.status,
            //             info: 'conversion options: ' + JSON.stringify(options),
            //         });
            //
            //         deferred.reject(API_ERROR_JOB_CAN_NOT_BE_MODIFIED);
            //
            //         return;
            //     }
            // }

            if (e.response.status === 0) {
                globalLogger.log('qgApiHelper addConversionPromise fail - connection problem', e.response.status);

                reject(API_ERROR_CONNECTION_PROBLEM);

                return;
            }

            if (e.response.status === 402) {
                globalLogger.log('qgApiHelper addConversionPromise fail - payment required');

                reject({ message: API_ERROR_PAYMENT_REQUIRED, responseData: e.response.data });

                return;
            }

            globalLogger.log('qgApiHelper addConversionPromise fail - unknown', e.response.data);

            reject(API_ERROR_UNKNOWN);
        }
    });
};

SatApiHelper.prototype.uploadBase64Promise = async function (base64UploadUrl, data, token) {
    return new Promise(async (resolve, reject) => {
        /**
         * @type {AxiosConfig}
         */
        const config = {
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Methods': 'GET, POST, PATCH, PUT, DELETE, OPTIONS',
                'Access-Control-Allow-Headers': 'Origin, Content-Type, X-Auth-Token',
                'Access-Control-Allow-Credentials': true,
                'X-Oc-Token': token,
            },
            withCredentials: false,
        };

        try {
            const response = await axiosWrapper.post(base64UploadUrl, data, config);
            resolve(response.data);
        } catch (e) {
            reject(e);
        }
    });
};

export { SatApiHelper };
